// services/billing.ts
import { AuthFetch, publicFetch } from "../hooks/useApi";
import {
  AssinaturaStatusResponse,
  PlanosResponse,
  CheckoutRequest,
  CheckoutResponse,
  CheckoutStatusResponse,
  PortalResponse,
  ArrependimentoResponse,
  CancelarRequest,
  CancelarResponse,
  TrocarPlanoResponse,
  AcaoAssinaturaResponse,
  PlanoChave,
  FaturasResponse,
  ConviteResumoResponse,
  ClinicaResponse,
  ConviteAcessoRequest,
  ConviteAcessoResponse,
  ConvitesAcessoAbertosResponse,
  EntrarClinicaResponse,
} from "../types/billing.types";

export const billingService = {

  getAssinatura: (
    apiFetch: AuthFetch,
  ): Promise<AssinaturaStatusResponse | null> =>
    apiFetch<AssinaturaStatusResponse | null>(`/billing/assinatura`, {
      method: "GET",
    }),

  // Público — catálogo da tela /planos e da landing. Sem Authorization.
  getPlanos: (): Promise<PlanosResponse> =>
    publicFetch<PlanosResponse>(`/billing/planos`, {
      method: "GET",
    }),

  postCheckout: (
    apiFetch: AuthFetch,
    payload: CheckoutRequest,
  ): Promise<CheckoutResponse> =>
    apiFetch<CheckoutResponse>(`/billing/checkout`, {
      method: "POST",
      body:   JSON.stringify(payload),
    }),

  getCheckoutStatus: (
    apiFetch: AuthFetch,
    sessionId: string,
  ): Promise<CheckoutStatusResponse> =>
    apiFetch<CheckoutStatusResponse>(`/billing/checkout/${sessionId}/status`, {
      method: "GET",
    }),

  postPortal: (
    apiFetch: AuthFetch,
  ): Promise<PortalResponse> =>
    apiFetch<PortalResponse>(`/billing/portal`, {
      method: "POST",
    }),

  postArrependimento: (
    apiFetch: AuthFetch,
  ): Promise<ArrependimentoResponse> =>
    apiFetch<ArrependimentoResponse>(`/billing/arrependimento`, {
      method: "POST",
    }),

  postCancelar: (
    apiFetch: AuthFetch,
    payload: CancelarRequest,
  ): Promise<CancelarResponse> =>
    apiFetch<CancelarResponse>(`/billing/cancelar`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  postTrocarPlano: (
    apiFetch: AuthFetch,
    planoChave: PlanoChave,
  ): Promise<TrocarPlanoResponse> =>
    apiFetch<TrocarPlanoResponse>(`/billing/trocar-plano`, {
      method: "POST",
      body: JSON.stringify({ plano_chave: planoChave }),
    }),

  postReativar: (
    apiFetch: AuthFetch,
  ): Promise<AcaoAssinaturaResponse> =>
    apiFetch<AcaoAssinaturaResponse>(`/billing/reativar`, {
      method: "POST",
    }),

  deleteTrocaAgendada: (
    apiFetch: AuthFetch,
  ): Promise<AcaoAssinaturaResponse> =>
    apiFetch<AcaoAssinaturaResponse>(`/billing/troca-agendada`, {
      method: "DELETE",
    }),

  getFaturas: (
    apiFetch: AuthFetch,
  ): Promise<FaturasResponse> =>
    apiFetch<FaturasResponse>(`/billing/faturas`, {
      method: "GET",
    }),

  // Público — link de convite (/admin/billing/convites) pode ser aberto por
  // quem ainda não logou. Único método daqui que não recebe `apiFetch`.
  getConvite: (
    token: string,
  ): Promise<ConviteResumoResponse> =>
    publicFetch<ConviteResumoResponse>(`/billing/convites/${encodeURIComponent(token)}`, {
      method: "GET",
    }),

  // `confirmarCancelamentoAvulso`: o médico viu o aviso de que o plano
  // individual dele será cancelado, sem reembolso (resposta `plano-avulso-ativo`).
  postAceitarConvite: (
    apiFetch: AuthFetch,
    token: string,
    confirmarCancelamentoAvulso = false,
  ): Promise<CheckoutResponse> =>
    apiFetch<CheckoutResponse>(`/billing/convites/${encodeURIComponent(token)}/aceitar`, {
      method: "POST",
      body: JSON.stringify({ confirmar_cancelamento_avulso: confirmarCancelamentoAvulso }),
    }),

  // ── Clínica (dono / admin_cobranca) ───────────────────────────────────
  // A clínica sai do token no backend — nenhum id trafega daqui.
  getClinica: (
    apiFetch: AuthFetch,
  ): Promise<ClinicaResponse> =>
    apiFetch<ClinicaResponse>(`/billing/clinica`, {
      method: "GET",
    }),

  // O dono tira um médico convidado da própria clínica; ele fica independente, no Básico.
  deleteMembroClinica: (apiFetch: AuthFetch, medicoId: number): Promise<{ status: string }> =>
    apiFetch<{ status: string }>(`/billing/clinica/membros/${encodeURIComponent(String(medicoId))}`, {
      method: "DELETE",
    }),

  postAceitarConviteAcesso: (
    apiFetch: AuthFetch,
    token: string,
    confirmarCancelamentoAvulso = false,
  ): Promise<EntrarClinicaResponse> =>
    apiFetch<EntrarClinicaResponse>(
      `/billing/convites-acesso/${encodeURIComponent(token)}/aceitar`,
      {
        method: "POST",
        body: JSON.stringify({ confirmar_cancelamento_avulso: confirmarCancelamentoAvulso }),
      },
    ),

  getConvitesAcessoAbertos: (
    apiFetch: AuthFetch,
  ): Promise<ConvitesAcessoAbertosResponse> =>
    apiFetch<ConvitesAcessoAbertosResponse>(`/billing/convites-acesso`, {
      method: "GET",
    }),

  postConviteAcesso: (
    apiFetch: AuthFetch,
    payload: ConviteAcessoRequest,
  ): Promise<ConviteAcessoResponse> =>
    apiFetch<ConviteAcessoResponse>(`/billing/convites-acesso`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};
